import { Link } from "react-router-dom";
import { CheckCircle2, Compass, Target, HeartHandshake, Award, Truck, Building2, Sparkles, Users, Store, Shield, MapPin, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import PageHero from "@/components/shared/PageHero";
import { Section, Reveal } from "@/components/shared/Section";
import { Button } from "@/components/ui/button";
import { useSite } from "@/contexts/SiteContext";

export default function About() {
  const { settings } = useSite();

  return (
    <div>
      <PageHero
        label="About Hermanos Industries"
        title="Bharat's trusted B2B wholesale garment supplier."
        subtitle="For nearly a decade, we've powered the growth of retailers, boutiques, and distributors across every state of India — with original brands, honest pricing, and enterprise-grade service."
        breadcrumbs={[{ label: "About" }]}
      />

      {/* Overview */}
      <Section testId="about-overview">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <p className="section-label mb-4"><span className="inline-block w-8 h-px bg-gold align-middle mr-3" />Our Story</p>
            <h2 className="serif-display text-4xl md:text-5xl text-foreground">
              A garment wholesale business built on <span className="gold-text">trust, transparency, and scale.</span>
            </h2>
            <div className="mt-8 space-y-5 text-foreground/70 leading-relaxed text-base md:text-lg">
              <p>Hermanos Industries Pvt Ltd was established with a single ambition — to make premium branded garments accessible to every Indian retailer at wholesale rates.</p>
              <p>Headquartered in New Delhi, we serve as a bulk supplier and distributor to over <span className="text-gold font-semibold">10,000 retail businesses</span> — from Tier-1 metros to Tier-3 townships across all 28+ Indian states.</p>
              <p>We are a strict <strong>B2B wholesale operation</strong>. No retail. No ecommerce. No shortcuts. Just clean, high-volume garment distribution across 40+ trusted brands.</p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/request-catalogue">
                <Button className="rounded-sm h-11 bg-gold hover:bg-gold-400 text-navy-900 font-semibold tracking-wider uppercase text-xs px-6">Request Catalogue</Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="rounded-sm h-11 border-gold/40 hover:border-gold tracking-wider uppercase text-xs px-6">Visit Our Office</Button>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative overflow-hidden rounded-md aspect-[4/5] border border-border/60">
              <img src="https://images.unsplash.com/photo-1587293852726-70cdb56c2866" alt="Warehouse operations" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6">
                <p className="section-label text-gold">Since 2016</p>
                <p className="mt-2 font-serif text-3xl text-white">Powering retail across Bharat</p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Vision Mission Values */}
      <Section className="bg-secondary/30" label="Our Foundation" title="Vision, mission, and the values we operate by.">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: Compass, title: "Our Vision", desc: "To become India's most trusted enterprise B2B partner for readymade garment retailers, distributors, and boutique businesses." },
            { icon: Target, title: "Our Mission", desc: "Deliver original branded garments at wholesale rates with transparent pricing, dependable logistics, and long-term partnership." },
            { icon: HeartHandshake, title: "Our Values", desc: "Integrity, service, transparency, quality, and unwavering commitment to every retail partner's business growth." },
          ].map((v, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="h-full p-8 rounded-md border border-border/60 bg-card hover:border-gold/60 transition-colors">
                <v.icon className="text-gold" size={26} strokeWidth={1.4} />
                <h3 className="mt-5 font-serif text-2xl text-foreground">{v.title}</h3>
                <p className="mt-3 text-foreground/70 leading-relaxed">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Strengths */}
      <Section label="Why Businesses Choose Us" title="Enterprise strengths that scale your retail.">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: Award, t: "40+ Brands", d: "Multi-brand curated catalogue — always fresh, always original." },
            { icon: Truck, t: "Pan-India Logistics", d: "Reliable delivery across every Indian state." },
            { icon: Shield, t: "Original & Billed", d: "Authentic tags. Proper invoicing. Zero grey stock." },
            { icon: Building2, t: "Bulk Warehousing", d: "Ready inventory for large-volume dispatches." },
            { icon: Sparkles, t: "Seasonal Refresh", d: "New arrivals across categories every month." },
            { icon: Users, t: "Dealer Support", d: "Dedicated relationship manager for each partner." },
            { icon: Store, t: "10,000+ Retailers", d: "Trusted by boutiques, shops, and distributors nationwide." },
            { icon: HeartHandshake, t: "Long-term Partnership", d: "We grow with you — not just transact with you." },
          ].map((s, i) => (
            <Reveal key={i} delay={i * 0.04}>
              <div className="h-full p-6 rounded-md border border-border/60 bg-card hover:border-gold/60 hover:-translate-y-1 transition-all duration-300">
                <div className="h-10 w-10 rounded-sm bg-gold/10 text-gold flex items-center justify-center mb-4">
                  <s.icon size={18} strokeWidth={1.5} />
                </div>
                <h4 className="font-serif text-xl text-foreground">{s.t}</h4>
                <p className="mt-2 text-sm text-foreground/70">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Timeline / Business process */}
      <Section className="bg-secondary/30" label="Our Business Process" title="How partnership works at Hermanos.">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { n: "01", t: "Business Enquiry", d: "Share your retail profile via catalogue or quotation form." },
            { n: "02", t: "Assortment Planning", d: "Our team designs the ideal SKU mix for your market." },
            { n: "03", t: "Order Confirmation", d: "MOQ, pricing, delivery timeline finalized transparently." },
            { n: "04", t: "Pan-India Dispatch", d: "Bulk pack. Full invoice. Reliable last-mile delivery." },
          ].map((s, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="relative pt-8 border-t border-gold/40">
                <span className="absolute -top-4 left-0 font-serif text-5xl text-gold/50">{s.n}</span>
                <h3 className="font-serif text-2xl text-foreground">{s.t}</h3>
                <p className="mt-3 text-sm text-foreground/70 leading-relaxed">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Infrastructure & Location */}
      <Section label="Infrastructure & Reach" title="Delhi HQ. Pan India delivery.">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-6">
            <div className="space-y-5 text-foreground/80 leading-relaxed">
              <p className="text-lg">Our operations run from a strategic wholesale hub in New Delhi — enabling seamless connections with the largest brands and the widest logistics networks across India.</p>
              <ul className="space-y-3">
                {[
                  "Central New Delhi warehouse hub",
                  "Multi-brand ready-stock inventory",
                  "Bulk packaging & QC workflow",
                  "Direct courier and freight partnerships",
                  "Trusted by 10,000+ retail businesses",
                ].map((li, i) => (
                  <li key={i} className="flex gap-3">
                    <CheckCircle2 className="text-gold shrink-0 mt-0.5" size={18} />
                    <span>{li}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-6 border-t border-border/60 flex items-start gap-3">
                <MapPin className="text-gold shrink-0 mt-1" size={18} />
                <div>
                  <p className="section-label mb-1">Corporate Office</p>
                  <p className="text-foreground">{settings.address}</p>
                  <a href={settings.google_maps_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-2 text-xs tracking-wider uppercase text-gold hover:underline">
                    Get Directions <ArrowRight size={12} />
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="overflow-hidden rounded-md border border-border/60 aspect-[4/3]">
              <iframe
                title="Hermanos Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.8834569876826!2d77.16640287549533!3d28.63616897566367!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d030dc9db5495%3A0x2e05dab2a0b95c81!2sTodapur%2C%20New%20Delhi%2C%20Delhi%20110012!5e0!3m2!1sen!2sin!4v1700000000000"
                width="100%"
                height="100%"
                style={{ border: 0, filter: "grayscale(60%) contrast(1.05)" }}
                allowFullScreen=""
                loading="lazy" decoding="async"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <section className="relative py-20 md:py-24 border-y border-border/60 bg-navy text-white">
        <div className="absolute inset-0 grain opacity-30" />
        <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="relative mx-auto max-w-5xl px-4 md:px-8 text-center">
          <p className="section-label text-gold mb-4">Partner with Hermanos</p>
          <h3 className="serif-display text-4xl md:text-5xl text-white">Grow your retail with a wholesale ally.</h3>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/request-catalogue">
              <Button className="rounded-sm h-12 px-7 bg-gold hover:bg-gold-400 text-navy-900 font-semibold tracking-wider uppercase text-xs">Request Catalogue</Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="rounded-sm h-12 px-7 border-white/30 hover:bg-white/10 text-white tracking-wider uppercase text-xs">Contact Sales Team</Button>
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
